import { useEffect, useRef, useState } from "react";
import { nextNameDraft, resolveNameCommit } from "../../../routes/home/combatAuthoring";

/**
 * The inspector's free-text rename field (combatant `set_combatant_name` / room `set_room`); commits
 * on blur / Enter, non-empty. **Focus-guarded**: while the GM is editing
 * (focused), an external `name` change — a sync broadcast from another actor's rename, an undo, or an
 * optimistic/confirm race — does NOT overwrite the in-progress draft (the old
 * `useEffect(=> setDraft(name), [name])` clobbered keystrokes whenever `name` changed mid-edit).
 * The draft resyncs from props only when the field is idle. The decision surface is the two pure
 * `nextNameDraft` / `resolveNameCommit` helpers (`combatAuthoring.ts`), unit-tested without a DOM.
 *
 * **Ref-mirrored draft, second and distinct race.** `commit` runs
 * on blur, and blur can fire *synchronously* right after `onChange` (a fast programmatic or
 * fast-typing `fill → blur`) — before React has re-rendered the `<input>` with a closure over the
 * new `draft`. A `commit` built purely from the `useState` closure would then read the STALE
 * pre-keystroke draft and snap back with no command dispatched. `draftRef` is written synchronously
 * inside `onChange` (never via a `useEffect`, which is itself deferred) so `commit` always reads the
 * live latest value regardless of render timing; `draft` (the `useState`) stays the sole source of
 * what's rendered in `value={draft}`, and every other site that sets `draft` (the external-sync
 * effect, the snap-back branch) mirrors the same value into the ref so it never drifts from what's
 * on screen.
 */
export function NameField({ name, disabled, onCommit }: { name: string; disabled: boolean; onCommit: (name: string) => void }) {
  const [draft, setDraft] = useState(name);
  const draftRef = useRef(draft);
  const editing = useRef(false);
  // Adopt an external name only when idle — keep the draft while focused (no keystroke clobber).
  useEffect(() => {
    setDraft((d) => {
      const next = nextNameDraft(name, editing.current, d);
      draftRef.current = next;
      return next;
    });
  }, [name]);
  const commit = () => {
    editing.current = false;
    const r = resolveNameCommit(draftRef.current, name);
    if ("snapBack" in r) {
      draftRef.current = r.snapBack;
      return setDraft(r.snapBack); // empty / unchanged → snap back, no command
    }
    onCommit(r.commit);
  };
  return (
    <label className="npc-ctl-edit-field">
      <span className="npc-ctl-label">Name</span>
      <input
        value={draft}
        disabled={disabled}
        onFocus={() => {
          editing.current = true;
        }}
        onChange={(e) => {
          draftRef.current = e.target.value;
          setDraft(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        aria-label="unit name"
        className="npc-ctl-edit-input"
      />
    </label>
  );
}

/**
 * One EDIT-inspector base-stat field — an integer `>= min` committed on blur / Enter via
 * `edit_combatant_base`. **Ref-mirrored draft** (same fix as
 * {@link NameField}): `commit` reads `draftRef` — written synchronously inside `onChange` — rather
 * than the `useState` closure, so a blur that fires before React re-renders (a fast fill → blur)
 * can't read a stale pre-keystroke draft and snap back with no command dispatched. `draft` stays the
 * source of what's rendered in `value={draft}`; the value-prop-sync effect and the snap-back branch
 * both mirror into the ref too, so it never drifts from what's on screen.
 */
export function StatField({
  label,
  value,
  min,
  disabled,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  disabled: boolean;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = String(value);
    setDraft(String(value));
  }, [value]);
  const commit = () => {
    const n = Math.round(Number(draftRef.current));
    if (!Number.isFinite(n) || n < min || n === value) {
      draftRef.current = String(value);
      return setDraft(String(value)); // invalid / unchanged → snap back
    }
    onCommit(n);
  };
  return (
    <label className="npc-ctl-edit-stat">
      <span className="npc-ctl-edit-stat-label">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        value={draft}
        disabled={disabled}
        min={min}
        onChange={(e) => {
          draftRef.current = e.target.value;
          setDraft(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        aria-label={label}
        className="npc-ctl-edit-input npc-ctl-edit-stat-input"
      />
    </label>
  );
}
